import { DEPLOYMENT_KEYS } from 'utils/constants'
import { getChainDeploymentStudyScheduleIds } from 'utils/chainDeploymentUtils'

const visitPairKey = (visitId, visitScheduleId) => `${visitScheduleId || ''}.${visitId || ''}`

export const isExpireVisitInStudy = ({
  visit_id: visitId,
  visit_schedule_id: visitScheduleId,
  isVisitCycleSupported,
  studyVisitSchedule,
  visitTemplates,
}) => {
  if (!visitId || !visitScheduleId) return false

  if (isVisitCycleSupported) {
    if (!Array.isArray(studyVisitSchedule) || studyVisitSchedule.length === 0) return false
    return studyVisitSchedule.some(arm =>
      (arm.phases || []).some(phase =>
        (phase.cycles || []).some(
          cycle => cycle.id === visitScheduleId && (cycle.visits || []).some(visit => visit.id === visitId),
        ),
      ),
    )
  }

  if (!Array.isArray(visitTemplates) || visitTemplates.length === 0) return false
  return visitTemplates.some(
    template => template.id === visitScheduleId && (template.visits || []).some(visit => visit.id === visitId),
  )
}

export const getArmIdForVisitScheduleId = (studyVisitSchedule, visitScheduleId) => {
  if (!visitScheduleId || !Array.isArray(studyVisitSchedule)) return null

  for (const arm of studyVisitSchedule) {
    for (const phase of arm.phases || []) {
      if ((phase.cycles || []).some(cycle => cycle.id === visitScheduleId)) {
        return arm.id
      }
      if ((phase.visit_templates || []).some(template => template.id === visitScheduleId)) {
        return arm.id
      }
    }
  }

  return null
}

export const getArmsFromScheduleIds = (studyVisitSchedule = [], armIds = []) =>
  armIds
    .map(armId => {
      const arm = (studyVisitSchedule || []).find(item => item.id === armId)
      return arm ? { key: arm.id, text: arm.name } : null
    })
    .filter(Boolean)

export const getCyclesForArmIds = (studyVisitSchedule = [], armIds = []) =>
  armIds.reduce((acc, armId) => {
    const arm = (studyVisitSchedule || []).find(item => item.id === armId)
    const armCycles = arm?.phases?.reduce((phaseAcc, phase) => [...phaseAcc, ...(phase.cycles || [])], []) || []
    return [
      ...acc,
      ...armCycles.map(cycle => ({
        ...cycle,
        armId: arm?.id,
        armName: arm?.name,
      })),
    ]
  }, [])

export const collectExpireVisits = chainExpiryInfo => {
  if (Array.isArray(chainExpiryInfo?.expiry_condition?.terms)) {
    return chainExpiryInfo.expiry_condition.terms.map(term => ({
      visit_id: term.visit_id,
      visit_schedule_id: term.visit_schedule_id,
      study_schedule_id: term.study_schedule_id,
    }))
  }
  if (Array.isArray(chainExpiryInfo?.visits)) {
    return chainExpiryInfo.visits
  }
  if (chainExpiryInfo?.visit_id) {
    return [
      {
        visit_id: chainExpiryInfo.visit_id,
        visit_schedule_id: chainExpiryInfo.visit_schedule_id,
        study_schedule_id: chainExpiryInfo.study_schedule_id,
      },
    ]
  }
  return []
}

export const getArmsMissingExpireVisits = (armIds = [], expireVisits = [], resolveArmId) => {
  if (!Array.isArray(armIds) || armIds.length === 0) return []

  const armsWithVisits = new Set()
  expireVisits.forEach(visit => {
    const armId = visit.study_schedule_id || (resolveArmId && resolveArmId(visit))
    if (armId) armsWithVisits.add(armId)
  })

  return armIds.filter(armId => !armsWithVisits.has(armId))
}

const applyArmFields = (payload, armIds) => {
  if (armIds.length > 0) {
    payload[DEPLOYMENT_KEYS.studyScheduleIds] = armIds
  } else {
    delete payload[DEPLOYMENT_KEYS.studyScheduleIds]
  }

  if (armIds.length === 1) {
    payload[DEPLOYMENT_KEYS.studyScheduleId] = armIds[0]
  } else {
    delete payload[DEPLOYMENT_KEYS.studyScheduleId]
  }

  return payload
}

export const buildChainExpiryInfo = ({
  existing = {},
  armIds = [],
  visits = [],
  existingTerms = [],
} = {}) => {
  const terms = visits.map(visit => {
    const existingTerm = existingTerms.find(
      term => term.visit_id === visit.visit_id && term.visit_schedule_id === visit.visit_schedule_id,
    )
    if (existingTerm) {
      return {
        ...existingTerm,
        visit_id: visit.visit_id,
        visit_schedule_id: visit.visit_schedule_id,
        study_schedule_id: visit.study_schedule_id || existingTerm.study_schedule_id,
      }
    }
    return {
      visit_id: visit.visit_id,
      visit_schedule_id: visit.visit_schedule_id,
      study_schedule_id: visit.study_schedule_id,
    }
  })

  const payload = {
    ...existing,
    chain_expiry_by_ss_visit: true,
    [DEPLOYMENT_KEYS.visitIds]: visits.map(visit => visit.visit_id),
    expiry_condition: {
      operator: existing?.expiry_condition?.operator || 'OR',
      terms,
    },
  }

  if (visits.length === 1) {
    payload[DEPLOYMENT_KEYS.visitId] = visits[0].visit_id
    payload[DEPLOYMENT_KEYS.visitScheduleId] = visits[0].visit_schedule_id
  } else {
    delete payload[DEPLOYMENT_KEYS.visitId]
    delete payload[DEPLOYMENT_KEYS.visitScheduleId]
  }

  return applyArmFields(payload, armIds)
}

const sameIdList = (left = [], right = []) =>
  left.length === right.length && left.every((id, index) => id === right[index])

const sameVisitList = (left = [], right = []) =>
  left.length === right.length &&
  left.every(
    (visit, index) =>
      visit.visit_id === right[index].visit_id &&
      visit.visit_schedule_id === right[index].visit_schedule_id &&
      visit.study_schedule_id === right[index].study_schedule_id,
  )

/**
 * Keep expire visits that still belong to trigger arms and are not themselves
 * trigger visits. GOLD-12314: expire arms always mirror trigger arms.
 */
export const alignExpiryWithDeployment = ({
  chainExpiryInfo,
  deployArmIds = [],
  deployVisitPairs = [],
  resolveArmId,
} = {}) => {
  if (!chainExpiryInfo) return null

  const triggerPairs = new Set(
    deployVisitPairs.map(pair => visitPairKey(pair.visit_id, pair.visit_schedule_id)),
  )
  const currentVisits = collectExpireVisits(chainExpiryInfo)
  const nextVisits = currentVisits.filter(visit => {
    if (triggerPairs.has(visitPairKey(visit.visit_id, visit.visit_schedule_id))) {
      return false
    }
    const armId = visit.study_schedule_id || (resolveArmId && resolveArmId(visit))
    if (deployArmIds.length > 0 && armId && !deployArmIds.includes(armId)) {
      return false
    }
    return true
  })

  const currentArmIds = getChainDeploymentStudyScheduleIds(chainExpiryInfo)
  const visitsUnchanged = sameVisitList(currentVisits, nextVisits)
  const armsUnchanged = sameIdList(currentArmIds, deployArmIds)

  if (visitsUnchanged && armsUnchanged) return null

  return buildChainExpiryInfo({
    existing: chainExpiryInfo,
    armIds: deployArmIds,
    visits: nextVisits.map(visit => ({
      ...visit,
      study_schedule_id:
        visit.study_schedule_id || (resolveArmId && resolveArmId(visit)) || deployArmIds[0],
    })),
    existingTerms: chainExpiryInfo?.expiry_condition?.terms || [],
  })
}
